import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button, ChevronLeftIcon, Headings, Paragraph, Link } from '@sentara-health/optimahealth_web';
import { NavigateConst } from 'common';
import { useBranding } from 'context';

type CSRProxySuccessRouteState = {
    representativeName?: string;
    representativeEmail?: string;
};

const CSRProxySuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { brandConfig, brand } = useBranding();
    const routeState = (location?.state ?? {}) as CSRProxySuccessRouteState;

    const representativeName = useMemo(
        () => routeState.representativeName?.trim() ?? '',
        [routeState.representativeName]
    );
    const representativeEmail = useMemo(
        () => routeState.representativeEmail?.trim() ?? '',
        [routeState.representativeEmail]
    );
    const hasRepresentativeDetails = useMemo(
        () => representativeName.length > 0 && representativeEmail.length > 0,
        [representativeEmail, representativeName]
    );

    useEffect(() => {
        if (!hasRepresentativeDetails) {
            navigate(NavigateConst.dashboard, { replace: true });
            return;
        }
        window.scrollTo(0, 0);
        document.title = `${brandConfig.brandName} | ${t('profile.designatedRepresentative.successTitle')}`;
    }, [brandConfig.brandName, hasRepresentativeDetails, navigate, t]);

    if (!hasRepresentativeDetails) {
        return null;
    }

    return (
        <div className="col-12 my-md-2">
            <div className="d-flex justify-content-between align-items-center col-12 my-md-2">
                <Button
                    data-testid="success-back-button"
                    iconLeft={<ChevronLeftIcon />}
                    variant="icon_btn"
                    onClick={() => navigate(-1)}
                    className="ml-n1"
                >
                    {t('common.back')}
                </Button>
            </div>

            <div className="col-12 my-md-4 my-3">
                <Headings
                    level={1}
                    text={t('profile.designatedRepresentative.pageTitle')}
                    variant="semiBold"
                    className="pt-3"
                />
                <hr className="hr-1" />
            </div>

            <div
                className="col-xl-7 col-lg-8 col-12 px-md-4 mt-md-5 mt-4 pt-md-0 pt-2 pb-5"
            >
                <Paragraph
                    level={5}
                    variant="bold"
                    text={t('profile.designatedRepresentative.successTitle')}
                    className="mb-2"
                />
                <Paragraph
                    text={(
                        <Trans
                            i18nKey="profile.designatedRepresentative.designatedRepRequestSentDesc"
                            values={{
                                representativeName,
                                representativeEmail,
                            }}
                            components={{
                                email: (
                                    <Link
                                        to="#"
                                        className='pe-none'
                                    />
                                ),
                            }}
                        />
                    )}
                    variant="regular"
                    className="mb-md-3 mb-0"
                />
                <div className="mt-4 mb-5 pb-5">
                    <Link
                        to={NavigateConst.profile}
                        data-testid="go-to-profile-link"
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            navigate(-1);
                        }}
                    >
                        {t('dashboard.proxyAlertBannerActionButton')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CSRProxySuccess;